import { useEffect } from 'react';
import { Outlet, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useVendorAuthStore } from '../../store/vendorAuth.ts';
import { vendorPortalApi } from '../../features/vendorPortal/api.ts';
import { VendorSidebar } from '../../components/VendorSidebar.tsx';
import { VendorBottomNav } from '../../components/VendorBottomNav.tsx';
import { Icon } from '../../components/Icon.tsx';
import { t } from '../../lib/strings.ts';

// Material 3 top app bar for the vendor portal (desktop). Mark + wordmark on the
// inline-start (right, RTL); sign-out / avatar on the inline-end. Spans the full
// width above the navigation drawer. Mirrors WapMeet's staff TopBar, but is
// local to the vendor shell so it can read the vendor auth store.
//
// No theme toggle: the vendor portal is deliberately always light (see the
// force-light effect below), so a toggle here would be an inert control.
function VendorTopBar({ onLogout }: { onLogout: () => void }): JSX.Element | null {
  const vendor = useVendorAuthStore((s) => s.vendor);
  if (!vendor) return null;

  const initials = vendor.name.slice(0, 2).toUpperCase();

  return (
    <header
      className="hidden md:flex fixed top-0 inset-x-0 z-40 h-16 items-center gap-2 ps-3 pe-8 bg-[hsl(var(--chrome))]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <Link to="/vendor" className="flex items-center gap-3 ms-2 min-w-0">
        <img src="/icons/wappro-mark.svg" alt="" className="size-10 shrink-0 object-contain" />
        <span className="flex min-w-0 flex-col">
          <span className="wordmark-kolhu text-2xl text-ink leading-tight whitespace-nowrap">
            {t.vendorPortal.brand}
          </span>
          <span className="text-xs text-muted-foreground truncate leading-tight">
            {t.vendorPortal.brandSubtitle}
          </span>
        </span>
      </Link>

      <div className="flex-1" />

      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onLogout}
          aria-label={t.vendorPortal.logout}
          className="grid size-11 place-items-center rounded-full text-muted-foreground hover:bg-muted/70 transition-colors"
        >
          <Icon name="logout" size={22} />
        </button>
        <span
          aria-hidden
          title={vendor.name}
          className="grid size-9 ms-1 place-items-center rounded-full bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] text-sm font-medium"
        >
          {initials}
        </span>
      </div>
    </header>
  );
}

export function VendorLayout(): JSX.Element {
  const vendor = useVendorAuthStore((s) => s.vendor);
  const setVendor = useVendorAuthStore((s) => s.setVendor);
  const logout = useVendorAuthStore((s) => s.logout);
  const navigate = useNavigate();

  // The vendor portal is always light — vendors should not inherit a dark
  // system/staff preference. (We only drop the class; localStorage is left
  // alone so a staff member on the same browser keeps their own preference.)
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  // Keep identity fresh (name may be stale after re-login on another device).
  useQuery({
    queryKey: ['vendor-me'],
    queryFn: async () => {
      const me = await vendorPortalApi.me();
      setVendor({ id: me.id, name: me.name });
      return me;
    },
    staleTime: 5 * 60 * 1000,
  });

  function handleLogout(): void {
    logout();
    void navigate({ to: '/vendor/login' });
  }

  return (
    <div className={`min-h-screen flex flex-col ${vendor ? 'bg-[hsl(var(--chrome))]' : ''}`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:start-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium"
      >
        {t.nav.skipToMain}
      </a>

      <VendorTopBar onLogout={handleLogout} />
      <VendorSidebar />

      {/* Fixed inverted-corner masks so the white content panel reads as rounded
          against the chrome where the top app bar meets the nav drawer. Painted
          (not a panel border-radius) so the corner stays put as content scrolls.
          RTL: the drawer is on the inline-start (right), so the inner notch sits
          at right-64 and the outer notch at the left edge. */}
      {vendor && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 right-64 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom left, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      {vendor && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 left-0 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom right, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}

      {/* Mobile top app bar */}
      <header
        className="md:hidden sticky top-0 z-30 bg-[hsl(var(--chrome))]"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex h-14 items-center gap-1 px-1.5">
          <Link to="/vendor" aria-label={t.vendorPortal.brand} className="flex items-center gap-2 ps-1.5 min-w-0">
            <img src="/icons/wappro-mark.svg" alt="" className="size-8 shrink-0 object-contain" />
            <span className="flex min-w-0 flex-col">
              <span className="wordmark-kolhu text-[1.35rem] leading-none text-ink">{t.vendorPortal.brand}</span>
              <span className="text-[0.65rem] text-muted-foreground truncate leading-tight">
                {vendor?.name ?? t.vendorPortal.brandSubtitle}
              </span>
            </span>
          </Link>

          <div className="flex-1" />

          {vendor && (
            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                onClick={handleLogout}
                aria-label={t.vendorPortal.logout}
                className="grid size-11 place-items-center rounded-full text-muted-foreground active:bg-muted/60"
              >
                <Icon name="logout" size={20} />
              </button>
            </div>
          )}
        </div>
      </header>

      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${vendor ? 'has-bottom-nav md:ms-64 md:pt-16' : ''}`}
      >
        {vendor ? (
          <div className="min-h-dvh bg-[hsl(var(--background))] md:min-h-[calc(100dvh-4rem)]">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      <VendorBottomNav />
    </div>
  );
}
